import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, BarChart3, FileText, CheckCircle2, Search, Trophy } from 'lucide-react';
import { useCan, visibleTabs, pickActiveTab, NoAccess } from '../../lib/rbac';
import type { Tab } from './shared';
import { QueueTab } from './tabs/QueueTab';
import { DraftsTab } from './tabs/DraftsTab';
import { PublishedTab } from './tabs/PublishedTab';
import { FeederTab } from './tabs/FeederTab';
import { TopSellersTab } from './tabs/TopSellersTab';

export default function PublisherPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [tab, setTab] = useState<Tab>('queue');

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'queue', label: t('pub.pipeline'), icon: <BarChart3 size={16} /> },
    { key: 'drafts', label: t('pub.drafts'), icon: <FileText size={16} /> },
    { key: 'published', label: t('pub.published'), icon: <CheckCircle2 size={16} /> },
    { key: 'feeder', label: t('pub.discovery'), icon: <Search size={16} /> },
    { key: 'topsellers', label: t('pub.topsellers'), icon: <Trophy size={16} /> },
  ];

  const allowedTabs = visibleTabs('publisher', tabs, can);
  const active = pickActiveTab('publisher', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Rocket size={24} className="text-[var(--color-primary)]" />
        <h1 className="text-xl font-bold text-[var(--color-text)]">{t('nav.publisher')}</h1>
      </div>

      <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
        {allowedTabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              active === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {active === 'queue' && <QueueTab />}
      {active === 'drafts' && <DraftsTab />}
      {active === 'published' && <PublishedTab />}
      {active === 'feeder' && <FeederTab />}
      {active === 'topsellers' && <TopSellersTab />}
    </div>
  );
}
